import type {ReduxStore} from 'src/reducers';
import type {TypedIntent} from './agentic-ui-event-bus';
import * as React from 'react';
import {useHistory} from 'src/rerouter';
import logger from 'src/utils/logger';
import {useRegisterAgenticUIIntent} from 'src/components/agentic-ui';
import {sendMessage, returnToNormalChat} from './ask-ai-store';
import {setAIPanelOpenEvent} from 'src/action-creators/sense-ai-panel';
import {FULLSCREEN_MODE_KEY} from 'src/utils/ask-ai';


const AUTOMATION_WORKFLOWS_PATH = '/automation-workflows';

export const useHandleAskAiEvents = (reduxStore: ReduxStore) => {
  const router = useHistory();

  useRegisterAgenticUIIntent('graceai', 'graceai.selectprompt', (intent) => {
    const prompt = (intent.payload as {prompt: string}).prompt;
    if (prompt) {
      sendMessage(prompt);
    }
  });
  useRegisterAgenticUIIntent('graceai', 'graceai.generatecontent', (_) => {
    reduxStore.dispatch(setAIPanelOpenEvent(true));
  });
  // Action-card "Continue Conversation" (or a backend-emitted equivalent):
  // dismiss the docked sub-chat and return control to the composer.
  useRegisterAgenticUIIntent('graceai', 'graceai.continueconversation', () => {
    returnToNormalChat();
  });

  // Preview-card "open asset": route to the standalone asset viewer in
  // fullscreen chat mode, capturing the current page so close returns there.
  // We rely only on `asset_id` — the viewer fetches the asset by id.
  useRegisterAgenticUIIntent('graceai', 'graceai.resource.show', (intent) => {
    const {asset_id: assetId} = intent.payload as {asset_id?: string};
    if (!assetId) {
      return;
    }
    const sourceUrl = router.location.pathname;
    router.push({
      pathname: `/askai/asset-viewer/${assetId}`,
      query: {
        [FULLSCREEN_MODE_KEY]: 'true',
        sourceurl: sourceUrl,
      },
    });
  });

  // Cross-product navigation: the global handler only routes the user to the
  // target area, identified by the first key in `segment`. Once the destination
  // page mounts its own page-level handler, the user re-types their request
  // there. No buffering/replay here.
  useRegisterAgenticUIIntent('graceai', 'graceai.navigation', (intent) => {
    const {segment, workflow_id: workflowId} = intent.payload as {
      segment?: string[];
      workflow_id?: string;
    };

    switch (segment?.[0]) {
      case 'automationWorkflow':
        router.push({
          pathname: workflowId
            ? `${AUTOMATION_WORKFLOWS_PATH}/${workflowId}`
            : AUTOMATION_WORKFLOWS_PATH,
        });
        break;
      case 'fieldSyncAudit':
        router.push({pathname: '/fields/field-sync'});
        break;
      default:
        logger.warning('Unhandled graceai.navigation segment', {segment});
    }
  });
};
